import {
  ApiError,
  instantPurchase,
  type ApiClient,
  type FairnessRequest,
  type IframePoolOrder,
  type VolatilityLevel,
} from '../api';
import { trackPurchase } from '../tracking';
import { loadFairnessEnabled } from './enabled';
import { ensureTicket, takeTicket, type HeldTicket } from './tickets';

// Refused before any money moved, and a newer ticket fixes it.
const STALE_TICKET_CODES = new Set([
  'FAIRNESS_TICKET_EXPIRED',
  'FAIRNESS_TICKET_USED',
  'FAIRNESS_SEED_REUSED',
  'FAIRNESS_PARAMS_MISMATCH',
]);

function asRequest(ticket: HeldTicket): FairnessRequest {
  return { ticketId: ticket.ticket.id, clientSeed: ticket.clientSeed };
}

function staleTicket(err: unknown): boolean {
  return err instanceof ApiError && STALE_TICKET_CODES.has(err.code ?? '');
}

/** Shown when no ticket could be had: the purchase is not sent, nothing is charged. */
export const NO_TICKET_MESSAGE =
  "We couldn't lock in a provably fair roll for this pack. You weren't charged. Please try again.";

// Fail closed: with provably fair on, a purchase never goes out without a
// ticket — the player would get a pack with no Verify and no way to notice.
// One more request first, for a blip on the way.
async function requireTicket(client: ApiClient, offerId: string): Promise<HeldTicket> {
  const ticket = (await takeTicket(client, offerId)) ?? (await takeTicket(client, offerId));
  if (!ticket) throw new ApiError(503, 'FAIRNESS_UNAVAILABLE', NO_TICKET_MESSAGE);
  return ticket;
}

async function sendPurchase(
  client: ApiClient,
  poolOfferId: string | number,
  quantity: number,
  volatility: VolatilityLevel,
  idempotencyKey: string | undefined,
  openImmediately: boolean,
): Promise<IframePoolOrder> {
  if (!loadFairnessEnabled()) {
    return instantPurchase(client, poolOfferId, quantity, volatility, idempotencyKey, undefined, openImmediately);
  }

  const offerId = String(poolOfferId);
  try {
    const first = await requireTicket(client, offerId);
    try {
      return await instantPurchase(client, poolOfferId, quantity, volatility, idempotencyKey, asRequest(first), openImmediately);
    } catch (err) {
      if (!staleTicket(err)) throw err;
      const second = await requireTicket(client, offerId);
      return await instantPurchase(client, poolOfferId, quantity, volatility, idempotencyKey, asRequest(second), openImmediately);
    }
  } finally {
    // The next purchase of this pack ("buy another") finds its ticket ready.
    void ensureTicket(client, offerId);
  }
}

/**
 * `instant_purchase`, provably fair when the operator has it on: presents the
 * pack's ticket with the player's seed. A stale ticket (expired while the
 * player waited, pack price changed) is refused before the debit, so it is
 * swapped for a fresh one and sent once more. No ticket at all → the purchase
 * is not sent. Operators without provably fair get the plain call, arguments
 * untouched. Every real purchase passes here, so this is also where the
 * partner's pixels hear about it.
 */
export async function instantPurchaseWithFairness(
  client: ApiClient,
  poolOfferId: string | number,
  quantity: number = 1,
  volatility: VolatilityLevel = 'normal',
  idempotencyKey?: string,
  openImmediately: boolean = false,
): Promise<IframePoolOrder> {
  const order = await sendPurchase(client, poolOfferId, quantity, volatility, idempotencyKey, openImmediately);
  trackPurchase(order);
  return order;
}
